<template>
    <Select
        ref="sel"
        v-bind="$attrs"
        label-in-value
        :multiple="multiple"
        :value="selectValue"
        :class="prefixCls"
        @input="onInput"
        @on-clear="onClear"
        @on-open-change="onOpenChange"
        @on-option-click="onOptionClick"
        @on-search="onSearch"
    >
        <Option
            v-for="(item, index) in optionList"
            v-show="querying"
            :data-querying="querying"
            :key="index"
            :value="item[id]"
            :label="item[text]"
            :disabled="item.disabled"
        >
        </Option>
        <Tree
            ref="tree"
            v-show="!querying"
            :data="treeData"
            :render="optRender"
            :itemClass="itemClass"
            :container="dropEle"
            :show-checkbox="multiple"
            @on-select-change="onSelect"
            @on-check-change="onCheck"
        ></Tree>
    </Select>
</template>
<script>
import { listToTree, type, deepTraverse } from '../../utils/assist'
import { Select, Option } from '../select'
import Field from '../../mixins/field'
import Tree from '../tree'
// 支持filter高级搜索组件
import Filterable from '../filter/filterable'

export default {
    name: 'SelectTree',
    mixins: [Filterable, Field],
    inheritAttrs: false,
    composed: true,
    components: {
        Select,
        Option,
        Tree,
    },

    props: {
        value: {
            type: [String, Number],
            default: '',
        },
        list: {
            type: Array,
            default: () => [],
        },
        id: {
            type: String,
            default: 'id',
        },
        pId: {
            type: String,
            default: 'pid',
        },
        text: {
            type: String,
            default: 'name',
        },
        // 能否选中
        selectable: {
            type: Function,
        },
        // result是否使用全路径名称
        fullPathName: {
            type: Boolean,
            default: false,
        },
        // option是否显示全路径名称
        fullPathTitle: {
            type: Boolean,
            default: false,
        },
        // 是否多选
        multiple: {
            type: Boolean,
            default: false,
        },
        // 路径连接符号
        joinSymbol: {
            type: String,
            default: '-',
        },
        // title路径连接方法
        joinTitle: {
            type: Function,
        },
        // name路径连接方法
        joinName: {
            type: Function,
        },
    },

    data() {
        return {
            treeData: [],
            treeMap: [],
            optionList: [],

            querying: false,
            clickedOption: null,
            focusIndex: -1,
            dropEle: void 0,
        }
    },

    computed: {
        prefixCls() {
            return 'ivu-select-tree'
        },
        curText() {
            return this.getText(this.value)
        },
        // select value
        selectValue() {
            if (this.multiple) {
                var val = this.value + ''

                return val ? val.split(',') : []
            } else {
                return this.value
            }
        },
    },

    watch: {
        list() {
            this.buildData()
        },
        value() {
            if (this.multiple) {
                this.setCheckState()
            }
        },
    },

    methods: {
        // 数据解析
        buildData() {
            var me = this

            listToTree(me.list, {
                id: me.id,
                pId: me.pId,
                done: (tree, map, list) => {
                    // tree data
                    me.treeMap = map
                    me.treeData = tree

                    list.forEach((v) => {
                        // 树节点文本
                        v.__title__ = v[me.text]
                    })

                    // normalize
                    list.forEach((v, i) => {
                        // 是否可选
                        var selectable = v.selectable

                        if (type(v.selectable) == 'undefined') {
                            selectable = me.selectable
                        }

                        if (type(selectable) == 'function') {
                            selectable = selectable({
                                root: tree,
                                node: v,
                                data: v,
                            })
                        }

                        v.selectable = selectable

                        // 默认可选
                        if (type(v.selectable) == 'undefined') {
                            v.selectable = true
                        }
                        // 树节点文本
                        v.__title__ = v[me.text]
                        // 自动聚焦
                        if (me.value === v[me.id]) {
                            me.focusIndex = (flatState) =>
                                flatState.findIndex(
                                    (q) => q.node[me.id] === me.value
                                )
                        }

                        // 可选状态
                        me.$set(v, 'selectable', v.selectable)
                        // 禁用勾选
                        me.$set(v, 'disableCheckbox', !v.selectable)
                        // 勾选状态
                        me.$set(
                            v,
                            'checked',
                            (me.value + ',').indexOf(v[me.id] + ',') >= 0
                        )
                    })

                    // 全路径名称显示
                    deepTraverse(tree, (node, parent, options) => {
                        if (me.fullPathName) {
                            if (parent) {
                                if (this.joinName) {
                                    node[me.text] = this.joinName(node, parent)
                                } else {
                                    node[me.text] =
                                        parent[me.text] +
                                        this.joinSymbol +
                                        node[me.text]
                                }
                            }
                        }
                        if (me.fullPathTitle) {
                            if (parent) {
                                if (this.joinTitle) {
                                    node.__title__ = this.joinTitle(
                                        node,
                                        parent
                                    )
                                } else {
                                    node.__title__ =
                                        parent.__title__ +
                                        this.joinSymbol +
                                        node.__title__
                                }
                            }
                        }
                    })

                    // select options (多选只显示末级)
                    me.optionList = me.multiple
                        ? list.filter((v) => v.isEnd)
                        : list
                },
            })
        },
        // 设置勾选状态
        setCheckState() {
            var me = this
            this.$refs.tree.flatState.forEach((node) => {
                var v = node.node

                if ((me.value + ',').indexOf(v[me.id] + ',') >= 0) {
                    v.checked = true
                } else {
                    v.checked = false
                }
            })
        },
        // item渲染
        optRender(h, { root, node, data }) {
            var me = this

            return h(
                'div',
                {
                    on: {
                        click() {
                            // 单选
                            if (me.multiple === false && data.selectable) {
                                // 重复选择
                                if (me.value == data[me.id]) {
                                    return me.$refs.sel.hideMenu()
                                }
                                me.$refs.tree.handleSelect(node.nodeKey)
                            } else {
                                // 展开
                                me.$refs.tree.handleExpand(node.nodeKey)
                            }
                        },
                    },
                    style: {
                        padding: '8px 0',
                        lineHeight: '20px',
                    },
                },
                [
                    h(
                        'span',
                        {
                            style: {
                                padding: '0 4px',
                            },
                        },
                        data.__title__
                    ),
                ]
            )
        },
        // tree-option 样式
        itemClass({ data }) {
            var disabled = data.disabled,
                selected =
                    this.multiple === false && this.value == data[this.id]

            return {
                [this.prefixCls + '-item']: true,
                [this.prefixCls + '-item-selected']: selected,
                [this.prefixCls + '-item-disabled']: disabled,
            }
        },
        // 获取文本
        getText(id) {
            var opt = this.optionList.find((v) => v[this.id] == id)

            return opt ? opt[this.text] : id
        },
        // select listener
        onInput(val) {
            // 单选模式——修改定位位置
            if (this.multiple === false) {
                if (val) {
                    this.focusIndex = this.treeMap[val].nodeKey
                } else {
                    this.focusIndex = -1
                }
            }
        },
        // 清空
        onClear() {
            this.$nextTick(() => this.dispatchFormChange(''))
        },
        // 单选事件
        onSelect(e) {
            var node = e[0],
                value = node[this.id],
                txt = this.getText(value)

            if (node) {
                this.$emit('input', value)
                this.$emit('on-change', {
                    [this.id]: value,
                    [this.text]: txt,
                })
                this.dispatchFormChange(value)
                this.$refs.sel.hideMenu()
                this.focusIndex = node.nodeKey
            }
        },
        // 多选
        onCheck(checkedList, data) {
            var value = checkedList
                .reduce((acc, v) => {
                    // 只保存末级
                    if (v.isEnd) {
                        acc.push(v[this.id])
                    }

                    return acc
                }, [])
                .join(',')

            this.$emit('input', value)
            this.$emit('on-change', checkedList, data)
            this.dispatchFormChange(value)
        },
        // 下拉框
        onOpenChange(state) {
            // 展开
            if (state) {
                this.clickedOption = null

                // 自动定位到focusIndex
                if (this.multiple === false) {
                    this.$nextTick(() =>
                        this.$refs.tree.focusOn(this.focusIndex)
                    )
                }
            }
            // 收起
            else {
                // drop收起动画后再隐藏optionList
                if (this.clickedOption) {
                    setTimeout(() => {
                        this.querying = false
                    }, 300)
                }
            }
        },
        // 搜索
        onSearch(query) {
            // 开启搜索交互
            this.querying = true
        },
        // option 选项点击
        onOptionClick(option, checked) {
            // flag
            this.clickedOption = option
            // 同步更新tree的check状态
            if (this.multiple) {
                var tree = this.$refs.tree

                tree.handleCheck({
                    checked,
                    nodeKey: tree.flatState.find(
                        (v) => v.node[this.id] === option.value
                    ).nodeKey,
                })
            }
        },
        getSelectedNodes() {
            return this.$refs.tree.getSelectedNodes()
        },
        getCheckedNodes() {
            return this.$refs.tree.getCheckedNodes()
        },
        getCheckedAndIndeterminateNodes() {
            return this.$refs.tree.getCheckedAndIndeterminateNodes()
        },
    },

    mounted() {
        this.buildData()
        this.$nextTick(() => {
            this.dropEle = this.$refs.sel.$refs.dropdown.$el
        })
    },
}
</script>
